<template>
  <div>
    <div class="card-stripe">
      <div class="card-stripe__left-wrap">
        <div class="card-wrap">
          <div id="card-number" class="card-wrap__field" />
          <div id="card-date" class="card-wrap__field card-wrap__field_sm" />
          <div id="card-cvc" class="card-wrap__field card-wrap__field_sm" />
        </div>
        <div class="card-stripe__sign mb-3 mb-md-6">
          Powered by
          <img :src="require('@/assets/img/payments/stripe.svg')" />
        </div>
        <div class="card-stripe__errors">
          <slot name="errors" />
        </div>
        <slot />
        <slot name="confirm" />
      </div>
      <div class="card-stripe__right-wrap">
        <PaymentMethodCardAddress @initValidate="onInit" v-model="address" />
      </div>
    </div>
    <slot name="validator" :validate="validateMethod" />
  </div>
</template>

<script>
import useStripe from '@/compositions/accounting/useStripe';
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, toRefs,
} from '@vue/composition-api';
import PaymentMethodCardAddress from '@/components/Payment/Methods/PaymentMethodCardAddress';
import usePayment from '@/compositions/accounting/usePayment';

export default {
  name: 'StripePaymentCard',
  components: { PaymentMethodCardAddress },
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit, root: { $vuetify } }) {
    const { value } = toRefs(props);
    const { setPaymentAddress } = usePayment();
    const validateMethod = ref(null);
    const isUnmounted = ref(false);

    const address = computed({
      get() {
        return value.value || {};
      },
      set(val) {
        setPaymentAddress(val);
      },
    });

    const inputStyles = computed(() => {
      if ($vuetify.breakpoint.xs) {
        return {
          fontSize: '16px',
        };
      }
      return {
        fontSize: '20px',
      };
    });

    const {
      load,
      mount,
    } = useStripe({ inputStyles: inputStyles.value });

    onMounted(async () => {
      await load();
      await nextTick();
      if (isUnmounted.value) {
        return;
      }
      mount();
    });

    onBeforeUnmount(() => {
      isUnmounted.value = true;
    });

    function onInit(validate) {
      validateMethod.value = validate;
      emit('initValidate', validate);
    }

    return {
      address,
      validateMethod,
      onInit,
    };
  },
};
</script>

<style lang="scss" scoped>
  .card-stripe {
    display: flex;
    width: 100%;
    &__left-wrap {
      width: 50%;
      max-width: 40rem;
      margin-right: 1.875rem;
    }
    &__right-wrap {
      width: 50%;
      max-width: 35rem;
    }
    &__sign {
      img {
        display: inline-block;
        vertical-align: middle;
      }
    }
    &__errors {
      max-width: 27.375rem;
    }
  }
  .card-wrap {
    width: 100%;
    max-width: 27.375rem;
    padding: 1.875rem 2.625rem 0.5rem 2.625rem;
    background: rgba(238, 238, 238, 0.4);
    border: 1px solid #EEEEEE;
    border-radius: 0.5rem;
    box-shadow: 0 2px 0.5rem rgba(0, 0, 0, 0.16);
    margin-bottom: 0.625rem;
    &__field {
      background: #FFFFFF;
      border: 1px solid #C2C2C2;
      border-radius: 0.25rem;
      padding: 0.901rem 0.875rem;
      font-size: 1.5rem;
      line-height: 0.83;
      letter-spacing: 0.04em;
      color: #212121;
      margin-bottom: 1.875rem;
      max-height: 55px;
      &_sm {
        display: inline-block;
        width: 6.625rem;
        margin-right: 1.125rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .card-stripe {
      &__right-wrap {
        max-width: 23rem;
      }
      &__sign {
        font-size: 0.75rem;
        img {
          width: 3.5rem;
        }
      }
    }
    .card-wrap {
      max-width: 23rem;
      padding: 1.5rem 1.75rem 0.5rem 1.75rem;
      &__field {
        padding: 0.625rem 0.875rem;
        font-size: 0.9375rem;
        margin-bottom: 1.5rem;
        max-height: 3rem;
        &_sm {
          display: inline-block;
          margin-right: 1.5rem;
        }
      }
    }
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .card-stripe {
      display: block;
      &__left-wrap {
        width: 100%;
        margin-right: 0;
      }
      &__right-wrap {
        width: 100%;
      }
      &__errors {
        max-width: 100%;
      }
      &__sign {
        font-size: 0.75rem;
        img {
          width: 3rem;
          vertical-align: bottom;
        }
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .card-wrap {
      padding: 1rem 0.75rem 0.5rem 0.75rem;
      &__field {
        padding: 0.625rem 0.875rem;
        font-size: 0.9375rem;
        max-height: 2.5rem;
        &_sm {
          width: 5rem;
        }
      }
    }
  }
</style>
